/**
 * Skeleton component for ScopeCard loading state
 * Provides a loading placeholder while menu scope data is being fetched
 *
 * @module components/ScopeCard/ScopeCardSkeleton
 */

// MUI Components
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';

/**
 * Scope Card Skeleton Component
 * Renders a skeleton placeholder that matches the ScopeCard structure
 * Uses the exact dimensions from ScopeCard styles (320x252px)
 *
 * @returns {React.ReactElement} Scope card skeleton component
 *
 * @example
 * <ScopeCardSkeleton />
 */
const ScopeCardSkeleton = (): React.ReactElement => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        width: theme.typography.pxToRem(320),
        height: theme.typography.pxToRem(252),
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.typography.pxToRem(8),
        backgroundColor: theme.palette.common.white,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        gap: theme.spacing(2),
        border: `1px solid ${theme.palette.grey[400]}`,
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'end',
          textAlign: 'center',
          height: '100%',
          width: '100%',
          padding: `0 0 ${theme.spacing(2)} 0 !important`,
        }}
      >
        {/* Icon skeleton - matches the large icon size */}
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <Skeleton
            height={theme.typography.pxToRem(78)}
            sx={{
              bgcolor: 'grey.400',
              borderRadius: 2,
            }}
            variant="rectangular"
            width={theme.typography.pxToRem(114)}
          />
        </Box>

        {/* Title skeleton - matches card title */}
        <Skeleton
          height={theme.typography.pxToRem(24)}
          sx={{
            bgcolor: 'grey.400',
            marginBottom: theme.spacing(1),
            borderRadius: 2,
          }}
          variant="text"
          width={240}
        />

        {/* Description skeleton - 2 lines to match description */}
        <Skeleton
          height={theme.typography.pxToRem(18)}
          sx={{
            bgcolor: 'grey.400',
            marginBottom: theme.spacing(0.5),
            borderRadius: 2,
          }}
          variant="text"
          width={300}
        />
        <Skeleton
          height={theme.typography.pxToRem(18)}
          sx={{
            bgcolor: 'grey.400',
            borderRadius: 2,
          }}
          variant="text"
          width={240}
        />
      </CardContent>
    </Card>
  );
};

export default ScopeCardSkeleton;
